import { useCallback, useEffect, useRef, useState } from 'react'
import { Paperclip, ShieldAlert } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { languageLabel } from '@/shared/lib/language'
import { Drawer, DrawerField } from '@/shared/ui/Drawer'
import { api } from '@/shared/api/client'
import {
  attachFiles, listAttachments, listSources, markAttachment,
  type Attachment, type Item, type Source,
} from '../api'
import { CollectedAt, GradeChip, Pill, fmtDate, label } from './primitives'

/**
 * One item, in full — and its supporting files.
 *
 * ## Why this exists
 *
 * A dossier tab is a six-column table: grade, title, type, veracity, occurred, collected.
 * That is the right SUMMARY and a useless RECORD. The body, the attestation, the legal
 * basis, the collection method, the source's own grade and the custody chain are the half
 * that answers "can I use this in a briefing", and none of it fitted in a row. So the row
 * opens.
 *
 * ## Attachments are append-only
 *
 * Files can be attached at any time after the item was created, in any category, several at
 * once — the scan of a judgment usually turns up days after the record was typed in. There
 * is no delete: a file that turns out to be doctored, superseded or simply the wrong one is
 * marked NOT DEPENDABLE with a reason and stays visible with that mark. A reader who saw it
 * cited last month has to be able to learn why not to rely on it, and a vanished file cannot
 * tell them anything.
 *
 * ## The grade is repeated at the top
 *
 * It is on the row already, but a drawer is where somebody reads the body — which is exactly
 * the moment a `D5` claim starts to feel like a fact.
 */
export function ItemDetail({ item, open, onClose, canAttach, canMark }: {
  item: Item | null
  open: boolean
  onClose: () => void
  /** `intel:ingest`. Without it the attach control is absent, not a disabled tease. */
  canAttach?: boolean
  /** `intel:triage`. Marking is a judgement, not part of uploading. */
  canMark?: boolean
}) {
  const [attachments, setAttachments] = useState<Attachment[] | null>(null)
  // The item carries a sourceId. A uuid tells a reader nothing about whether to trust it,
  // and the source's own reliability is half of the Admiralty pair on the row.
  const [sources, setSources] = useState<Source[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [caption, setCaption] = useState('')
  const [marking, setMarking] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  /**
   * Open a stored file.
   *
   * NOT a plain `<a href>` at the API. `GET /files/{id}/download` answers with a 302 to the
   * object store, and an anchor cannot carry the bearer token to get that far. `/{id}/url`
   * hands back a short-lived presigned link as JSON, which we fetch WITH the token and then
   * open — the presigned link needs no auth of its own.
   */
  async function openFile(storageFileId: string) {
    try {
      const presigned = await api.get<{ url: string }>(`/files/${storageFileId}/url`)
      window.open(presigned.url, '_blank', 'noopener')
    } catch (err) {
      setError(explain(err))
    }
  }

  const load = useCallback(async () => {
    if (!item) return
    try {
      setError(null)
      setAttachments(await listAttachments(item.id))
    } catch (err) {
      setError(explain(err))
    }
  }, [item])

  useEffect(() => { if (open) void load() }, [open, load])
  useEffect(() => {
    if (!open || sources.length > 0) return
    void listSources().then(setSources).catch(() => setSources([]))
  }, [open, sources.length])

  async function upload(files: FileList | null) {
    if (!item || !files || files.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await attachFiles(item.id, Array.from(files), caption)
      setCaption('')
      if (fileInput.current) fileInput.current.value = ''
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function mark(attachmentId: string, dependable: boolean) {
    setBusy(true)
    setError(null)
    try {
      await markAttachment(attachmentId, dependable, reason)
      setMarking(null)
      setReason('')
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  /** `media` is stored JSON. Malformed is possible; an exception here is not. */
  const arrivedWith: { storageFileId: string; kind?: string; bytes?: number }[] = (() => {
    if (!item?.mediaJson) return []
    try {
      const parsed = JSON.parse(item.mediaJson) as { storageFileId: string; kind?: string;
        bytes?: number }[]
      return Array.isArray(parsed) ? parsed.filter((m) => m?.storageFileId) : []
    } catch {
      return []
    }
  })()

  if (!item) return null

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={item.title ?? '(untitled)'}
      subtitle={`${label(item.itemTypeCode)} · ${label(item.categoryCode)}`}
      right={<GradeChip grade={item.grading} />}
    >
      {error && <div className="p-3"><ErrorStrip message={error} /></div>}

      {item.redacted ? (
        // The content is gone but the ROW is not, and the reason is the whole point.
        <p className="m-3 rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2
                      text-erp-sm text-amber-700 dark:text-amber-400">
          Redacted — {item.redactedReason}
        </p>
      ) : (
        // ALWAYS rendered, even when empty. A section that disappears when there is nothing
        // in it is indistinguishable from a screen that forgot to show it, and "what does
        // this record actually say" is the question the drawer exists to answer.
        <div className="space-y-1 border-b border-border/60 px-4 py-3">
          {item.summary && <p className="text-erp-sm font-medium">{item.summary}</p>}
          {item.body ? (
            <p className="whitespace-pre-wrap text-erp-sm">{item.body}</p>
          ) : (
            <p className="text-erp-sm text-muted-foreground">
              No body was recorded on this item — only the title above.
              {item.originCode === 'MANUAL'
                && ' Manual entry leaves the body optional; the attestation below is what the '
                   + 'submitter said about it.'}
            </p>
          )}
          {item.extractedText && (
            <details className="mt-1">
              <summary className="cursor-pointer text-erp-xs text-muted-foreground">
                Text read from the scan by OCR
              </summary>
              <p className="mt-1 whitespace-pre-wrap rounded bg-muted p-2 text-erp-xs">
                {item.extractedText}
              </p>
            </details>
          )}
        </div>
      )}

      <dl>
        <DrawerField label="Veracity" value={<Pill className={
          item.veracityCode === 'DEBUNKED' || item.veracityCode === 'DISPUTED'
            ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
            : 'border-border'}>{label(item.veracityCode)}</Pill>} />
        <DrawerField label="Grade"
                     value={`${item.sourceReliability} source reliability · `
                            + `${item.infoCredibility} information credibility`} />
        {/* Two dates, never one. A single "last updated" answers neither question. */}
        <DrawerField label="Occurred"
                     value={item.occurredAt ? fmtDate(item.occurredAt)
                       : <span className="text-muted-foreground">date unknown — never guessed</span>} />
        <DrawerField label="Published"
                     value={item.publishedAt ? fmtDate(item.publishedAt) : null} />
        <DrawerField label="Custody" value={<CollectedAt at={item.collectedAt} />} />
        <DrawerField label="How it arrived"
                     value={`${label(item.originCode)} · ${label(item.collectionMethodCode)}`} />
        <DrawerField label="Legal basis" value={label(item.legalBasisCode)} />
        <DrawerField label="Sensitivity" value={label(item.sensitivityCode)} />
        {item.url && (
          <DrawerField label="Source link" value={
            <a href={item.url} target="_blank" rel="noreferrer"
               className="break-all text-primary underline">{item.url}</a>} />
        )}
        {item.attestationText && (
          // The submitter said how they know this, against their name. It is the reason a
          // manual entry is admissible at all, so it is shown, not hidden.
          <DrawerField label="Attested" value={
            <>
              <span className="italic">“{item.attestationText}”</span>
              {item.attestedAt && (
                <span className="block text-erp-xs text-muted-foreground">
                  {fmtDate(item.attestedAt)}
                </span>
              )}
            </>} />
        )}
        <DrawerField label="Source" value={(() => {
          const source = sources.find((row) => row.id === item.sourceId)
          return source
            ? `${source.name} (${source.code}) · reliability ${source.reliability}`
            // A source the caller cannot see is still a fact about the item.
            : item.sourceId
        })()} />
        {item.externalId && (
          <DrawerField label="Id at the source" value={item.externalId} mono />
        )}
        {item.language && <DrawerField label="Language" value={languageLabel(item.language)} />}
        {item.collectionRunId && (
          // Which scheduled fetch brought it in. The difference between "a collector found
          // this" and "somebody typed it" is already on the row above; this says WHICH run.
          <DrawerField label="Collection run" value={item.collectionRunId} mono />
        )}
        {item.corroboratesItemId && (
          <DrawerField label="Corroborates" value={item.corroboratesItemId} mono />
        )}
        {item.disputesItemId && (
          <DrawerField label="Disputes" value={item.disputesItemId} mono />
        )}
        {item.retentionUntil && (
          <DrawerField label="Held until" value={fmtDate(item.retentionUntil)} />
        )}
        {item.payloadJson && item.payloadJson !== '{}' && (
          <DrawerField label="Structured fields" value={
            <pre className="axis-table-scroll whitespace-pre-wrap rounded bg-muted p-1
                            text-erp-xs">{prettyJson(item.payloadJson)}</pre>} />
        )}
        <DrawerField label="Item id" value={item.id} mono />
      </dl>

      {/* The files the item ARRIVED with — an evidence upload puts its scan here, and until
          now nothing rendered it, so an uploaded document looked like an item with no file. */}
      {arrivedWith.length > 0 && (
        <div className="border-t border-border px-4 py-3">
          <div className="flex items-center gap-1.5">
            <Paperclip className="size-3.5 text-muted-foreground" />
            <span className="text-erp-sm font-medium">The file this item arrived with</span>
          </div>
          <ul className="mt-1 space-y-1">
            {arrivedWith.map((media) => (
              <li key={media.storageFileId}>
                <button type="button" onClick={() => void openFile(media.storageFileId)}
                        className="text-erp-sm text-primary underline">
                  {media.kind ?? 'File'} — {formatBytes(media.bytes ?? null)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-border px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Paperclip className="size-3.5 text-muted-foreground" />
          <span className="text-erp-sm font-medium">Supporting files</span>
          <span className="text-erp-xs text-muted-foreground">
            optional · any type · added at any time
          </span>
        </div>

        {attachments && attachments.length === 0 && (
          <p className="mt-1 text-erp-xs text-muted-foreground">
            Nothing attached. A photograph of the noticeboard, the scanned judgment or the
            recording all belong here.
          </p>
        )}

        <ul className="mt-2 space-y-1.5">
          {(attachments ?? []).map((a) => (
            <li key={a.id} className={`rounded border p-2 ${a.dependable
              ? 'border-border' : 'border-rose-500/40 bg-rose-500/5'}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <button type="button" onClick={() => void openFile(a.storageFileId)}
                        className={`text-left text-erp-sm ${a.dependable
                          ? 'text-primary underline'
                          : 'text-muted-foreground line-through'}`}>
                  {a.filename}
                </button>
                <span className="text-erp-xs text-muted-foreground">
                  {formatBytes(a.sizeBytes)}
                  {a.ocrStatus && ` · OCR ${a.ocrStatus.toLowerCase()}`}
                  {' · '}{fmtDate(a.attachedAt)}
                </span>
              </div>
              {a.caption && (
                <p className="text-erp-xs text-muted-foreground">{a.caption}</p>
              )}
              {!a.dependable && (
                <p className="mt-1 flex items-start gap-1 text-erp-xs text-rose-700
                              dark:text-rose-400">
                  <ShieldAlert className="mt-0.5 size-3 shrink-0" />
                  Not dependable — {a.undependableReason}
                </p>
              )}

              {canMark && (marking === a.id ? (
                <div className="mt-1.5 space-y-1">
                  {a.dependable && (
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why should nobody rely on this file?"
                      className="h-7 w-full rounded border border-input bg-background px-2
                                 text-erp-xs" />
                  )}
                  <div className="flex items-center gap-1.5">
                    <button type="button" disabled={busy
                      || (a.dependable && reason.trim().length < 20)}
                      onClick={() => void mark(a.id, !a.dependable)}
                      className="rounded bg-primary px-2 py-1 text-erp-xs
                                 text-primary-foreground disabled:opacity-50">
                      {a.dependable ? 'Mark not dependable' : 'Restore'}
                    </button>
                    <button type="button" onClick={() => { setMarking(null); setReason('') }}
                            className="rounded border border-border px-2 py-1 text-erp-xs">
                      Cancel
                    </button>
                    {a.dependable && (
                      <span className="text-erp-xs text-muted-foreground">
                        {reason.trim().length}/20 characters minimum
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setMarking(a.id)}
                        className="mt-1 text-erp-xs text-muted-foreground underline">
                  {a.dependable ? 'Mark not dependable' : 'Restore'}
                </button>
              ))}
            </li>
          ))}
        </ul>

        {canAttach && (
          <div className="mt-2 space-y-1.5 rounded border border-dashed border-border p-2">
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption for the files (optional)"
              className="h-7 w-full rounded border border-input bg-background px-2
                         text-erp-xs" />
            <input
              ref={fileInput}
              type="file"
              multiple
              disabled={busy}
              onChange={(e) => void upload(e.target.files)}
              className="text-erp-xs" />
            <p className="text-erp-xs text-muted-foreground">
              {/* Said plainly, because it is the rule people are most surprised by. */}
              Several at once is fine. Once attached a file cannot be deleted — if it turns
              out to be wrong, mark it not dependable and say why.
            </p>
          </div>
        )}
      </div>
    </Drawer>
  )
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
